import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatQty, variantLabel } from '@pos/core';
import { applyLocalChanges, type PurchaseRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, Input, PageHeader, Spinner, Toggle, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { buildPurchaseCostChanges, buildPurchaseVoidChanges, lineCost } from './changes.ts';
import { useStockContext } from './data.ts';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium', timeStyle: 'short' });

/** Compras cargadas (solo el dueño), con detalle, carga de costos y anulación. */
export function PurchasesPage() {
  const { business, writer } = useSession();
  const catalog = useCatalog();
  const money = useMoney();
  const context = useStockContext();
  const [openId, setOpenId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // Costos en edición, por línea de la compra (null = no se están editando).
  const [costs, setCosts] = useState<Map<string, number | null> | null>(null);
  const [updateCosts, setUpdateCosts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = useLiveQuery(async () => {
    const [purchases, items, members] = await Promise.all([
      db.purchases.where('business_id').equals(business.id).toArray(),
      db.purchase_items.where('business_id').equals(business.id).toArray(),
      db.memberships.where('business_id').equals(business.id).toArray(),
    ]);
    return { purchases, items, members };
  }, [business.id]);

  const view = useMemo(() => {
    if (!data || !catalog) return null;
    const suppliers = new Map(catalog.suppliers.map((s) => [s.id, s.name]));
    const people = new Map(data.members.map((m) => [m.user_id, m.display_name]));
    const itemsBy = new Map<string, typeof data.items>();
    for (const item of data.items) itemsBy.set(item.purchase_id, [...(itemsBy.get(item.purchase_id) ?? []), item]);
    const variants = new Map(
      catalog.items.flatMap((i) =>
        i.variants.map((v) => {
          const attrs = variantLabel(v.attributes);
          return [v.id, { unit: i.product.unit_type, name: attrs ? `${i.product.name} · ${attrs}` : i.product.name }] as const;
        }),
      ),
    );
    return {
      purchases: [...data.purchases].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)),
      supplier: (id: string | null) => (id ? (suppliers.get(id) ?? 'Proveedor eliminado') : 'Sin proveedor'),
      person: (id: string | null | undefined) => (id ? (people.get(id) ?? '—') : '—'),
      items: (id: string) => itemsBy.get(id) ?? [],
      describe: (variantId: string) => variants.get(variantId) ?? { unit: 'unit' as const, name: 'Producto eliminado' },
    };
  }, [data, catalog]);

  if (!view || !catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  // Se busca en los datos vivos: después de cargar costos o anular, el detalle se actualiza solo.
  const open: PurchaseRow | undefined = openId ? view.purchases.find((p) => p.id === openId) : undefined;
  const openItems = open ? view.items(open.id) : [];

  const close = () => {
    setOpenId(null);
    setCosts(null);
    setReason('');
    setError(null);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      requestSync(0);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const voidPurchase = () =>
    run(async () => {
      if (!open) return;
      await applyLocalChanges(db, buildPurchaseVoidChanges(open, openItems, reason, context()), writer);
      setReason('');
    });

  // Sugerencia: el costo que ya tenía la línea o, si no tenía, el costo actual del producto.
  const startCosts = () => {
    setCosts(new Map(openItems.map((it) => [it.id, it.unit_cost ?? catalog.costs.get(it.variant_id) ?? null])));
    setUpdateCosts(true);
    setError(null);
  };

  const saveCosts = () =>
    run(async () => {
      if (!open || !costs) return;
      const changes = buildPurchaseCostChanges(open, openItems, costs, { updateCosts, knownCosts: catalog.costs }, context());
      if (changes.length > 0) await applyLocalChanges(db, changes, writer);
      setCosts(null);
    });

  const editTotal =
    costs && openItems.every((it) => costs.get(it.id) != null)
      ? openItems.reduce((sum, it) => sum + lineCost(costs.get(it.id)!, it.qty_milli), 0)
      : null;

  let footer = null;
  if (open && costs) {
    footer = (
      <>
        <Button variant="secondary" onClick={() => setCosts(null)}>
          Cancelar
        </Button>
        <Button onClick={saveCosts} loading={busy}>
          Guardar costos
        </Button>
      </>
    );
  } else if (open && !open.voided_at) {
    footer = (
      <>
        <Input placeholder="Motivo para anular" value={reason} onChange={(e) => setReason(e.target.value)} compact />
        <Button variant="danger" onClick={voidPurchase} loading={busy}>
          Anular
        </Button>
      </>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader
        title="Compras"
        description="Lo que cargaste (vos y tus empleados). Anular una compra descuenta del stock lo que había ingresado."
        actions={
          <>
            <Link to="/stock">
              <Button variant="secondary">Volver</Button>
            </Link>
            <Link to="/stock/compra">
              <Button>Cargar compra</Button>
            </Link>
          </>
        }
      />
      <Card flush className="divide-y divide-slate-100">
        {view.purchases.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Todavía no hay compras cargadas.</p>}
        {view.purchases.map((p) => (
          <button key={p.id} type="button" onClick={() => setOpenId(p.id)} className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50 sm:px-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {view.supplier(p.supplier_id)}
                {p.invoice_number && <span className="text-slate-500"> · {p.invoice_number}</span>}
              </p>
              <p className="truncate text-sm text-slate-500">
                {dateFormat.format(new Date(p.occurred_at))} · {view.items(p.id).length} productos · cargó {view.person(p.created_by)}
              </p>
            </div>
            <div className="shrink-0 text-right">
              {p.total === null && !p.voided_at ? (
                <Badge tone="amber">Faltan costos</Badge>
              ) : (
                <p className="font-semibold">{p.total === null ? 'Sin costos' : money(p.total)}</p>
              )}
              {p.voided_at && <Badge tone="red">Anulada</Badge>}
            </div>
          </button>
        ))}
      </Card>

      <Dialog
        open={!!open}
        onClose={close}
        title={open ? `${view.supplier(open.supplier_id)}${open.invoice_number ? ` · ${open.invoice_number}` : ''}` : ''}
        footer={footer}
      >
        {open && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              {dateFormat.format(new Date(open.occurred_at))} · cargó {view.person(open.created_by)}
            </p>
            {open.voided_at && <Alert tone="warning">Anulada: {open.void_reason}</Alert>}
            {!open.voided_at && !costs && open.total === null && (
              <Alert tone="info">
                La cargó un empleado: registró las cantidades (el stock ya subió) pero no los costos. Cargalos vos para tener el
                total de la compra y, si querés, actualizar el costo de los productos.
              </Alert>
            )}
            <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
              {openItems.map((it) => {
                const d = view.describe(it.variant_id);
                if (costs) {
                  const cost = costs.get(it.id) ?? null;
                  return (
                    <li key={it.id} className="flex items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate">
                          {formatQty(it.qty_milli, d.unit)} × {d.name}
                        </p>
                        {cost !== null && <p className="text-xs text-slate-500">Subtotal {money(lineCost(cost, it.qty_milli))}</p>}
                      </div>
                      <div className="w-32 shrink-0">
                        <MoneyInput
                          aria-label={`Costo unitario de ${d.name}`}
                          placeholder="Costo c/u"
                          value={cost}
                          invalid={cost === null}
                          onChange={(c) => setCosts((m) => new Map(m).set(it.id, c))}
                        />
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={it.id} className="flex justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 truncate">
                      {formatQty(it.qty_milli, d.unit)} × {d.name}
                    </span>
                    <span className="shrink-0 text-slate-600">{it.unit_cost === null ? 'sin costo' : money(lineCost(it.unit_cost, it.qty_milli))}</span>
                  </li>
                );
              })}
            </ul>
            {costs ? (
              <div className="space-y-3">
                <p className="text-right text-base font-semibold">Total: {editTotal === null ? '—' : money(editTotal)}</p>
                <Toggle
                  checked={updateCosts}
                  onChange={setUpdateCosts}
                  label="Actualizar el costo de los productos"
                  description="Estos costos pasan a ser el costo de cada producto."
                />
              </div>
            ) : (
              <>
                {open.total !== null && <p className="text-right text-base font-semibold">Total: {money(open.total)}</p>}
                {!open.voided_at && (
                  <Button variant={open.total === null ? 'primary' : 'secondary'} size="sm" onClick={startCosts}>
                    {open.total === null ? 'Cargar costos' : 'Corregir costos'}
                  </Button>
                )}
              </>
            )}
            {open.notes && <p className="text-slate-600">Notas: {open.notes}</p>}
            {error && <Alert tone="error">{error}</Alert>}
          </div>
        )}
      </Dialog>
    </div>
  );
}
